import type { SubtitlePropsTypes } from '@Shelly/shelly-design-system/AdvancedCard/AdvancedCard.types';
import { get } from 'svelte/store';
import {
	getIsCalibrationFromstatus,
	real_id,
	is_gen_2,
	getIsDimmerCalibratingFromStatus,
	get_mode,
	is_gen_z,
	isBluTRV,
	isWallDisplay,
	is_gen_1,
} from 'src/helpers/interface_helpers';
import { checkDeviceForceUpdate } from 'src/helpers/firmware_helpers';
import {
	accessibleVirtualDevice,
	isExtractedDeviceComponentMissing,
	isVirtualDevice,
} from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { capFirstChar } from 'src/helpers/utilities';
import { format } from 'svelte-i18n';
import type { Device } from 'src/types/Device';

const _format = get(format);

export const defaultStateType = 99;
export const offlineStateType = 0;

const defaultOptionalTexts: SubtitlePropsTypes = { value: '', type: 'default' };

export type DeviceStateConfig = {
	subtitle: SubtitlePropsTypes;
	hideSlotMain: boolean | undefined;
	hideSlotRight: boolean | undefined;
	isLoading: boolean | undefined;
	type: number | undefined;
	description?: SubtitlePropsTypes;
	optional?: SubtitlePropsTypes;
	icon: string | undefined;
	iconColor: string | undefined;
};

export function createDefaultDeviceStateConfig(): DeviceStateConfig {
	return {
		subtitle: { ...defaultOptionalTexts },
		hideSlotMain: undefined,
		hideSlotRight: undefined,
		isLoading: undefined,
		type: undefined,
		description: { ...defaultOptionalTexts },
		optional: { ...defaultOptionalTexts },
		icon: undefined,
		iconColor: undefined,
	};
}

export const availableStatuses: (device: Device) => Record<number | string, string> = (device) => {
	return {
		0: capFirstChar(
			_format('device_status_no_connection', {
				default: `Device is offline`,
			}),
		),
		1: capFirstChar(
			_format('device_status_pending_connection', {
				default: 'Pending connection',
			}),
		),
		2: capFirstChar(
			_format('device_status_rebooting', {
				default: 'Device is rebooting',
			}),
		),
		3: capFirstChar(
			_format('device_status_updating', {
				default: 'Device is updating',
			}),
		),
		4: capFirstChar(
			_format('device_status_calibrating', {
				default: 'Device is calibrating',
			}),
		),
		5: capFirstChar(
			_format('device_status_locked', {
				default: 'Device is locked',
			}),
		),
		6: 'Pin Locked',
		7: capFirstChar(
			_format('device_status_unsupported', {
				default: 'Firmware is not supported. Update required',
			}),
		),
		8: capFirstChar(
			_format('device_status_loading_data', {
				default: 'Loading data',
			}),
		),
		9: capFirstChar(
			_format('device_status_unauthorized', {
				default: 'Not authenticated device. See devices code',
			}),
		),
		10: capFirstChar(
			_format('device_status_updating_wifi', {
				default: `Device is updating it's Wi-Fi settings`,
			}),
		),
		11: capFirstChar(
			_format('device_status_cloud_associating', {
				default: `Device is associating with your account`,
			}),
		),
		12: capFirstChar(
			_format('device_status_inclusion_completed', {
				default: `Device is connecting to gateway`,
			}),
		),
		13: capFirstChar(
			_format('device_status_inclusion_completed', {
				default: `Inclusion has finished`,
			}),
		),
		14: capFirstChar(
			_format('device_status_disabled', {
				default: 'Device is disabled',
			}),
		),
		15: capFirstChar(
			_format('device_component_missing', {
				default: 'Device component is missing',
			}),
		),
		defaultStateType: 'Default Status',
	};
};

export function resolveDeviceStateLookupId(device: Device): string {
	if (
		isBluTRV(device?.type) ||
		isVirtualDevice(device?.type) ||
		isPeripheralDevice(device?.type) ||
		isWallDisplay(device?.type)
	) {
		return device?.id;
	}

	return real_id(device?.id);
}

export type DeviceStateComputeContext = {
	loadingData: boolean;
	status: Record<string, unknown> | undefined;
	settings: Record<string, unknown> | undefined;
	isRebooting: boolean;
	isLocked: boolean;
	isPending: boolean;
	isUpdating: boolean;
	updatingEntry: { prog?: number } | undefined;
	isCalibrating: boolean;
	isDimmerCalibrating: boolean;
	isUnauthorized: boolean;
	isAssociating: boolean;
	isUpdatingWifi: boolean;
	zWaveInclusionStatus: unknown;
};

export function computeDeviceState(device: Device, ctx: DeviceStateComputeContext): DeviceStateConfig {
	const deviceStateConfig = createDefaultDeviceStateConfig();
	const mode = get_mode(device, ctx.status, ctx.settings);

	if (ctx.loadingData) {
		deviceStateConfig.type = 8;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'disabled',
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = true;
	} else if (
		(!ctx.status?.online || typeof ctx.settings === 'undefined') &&
		!ctx.isCalibrating &&
		!ctx.isLocked &&
		!ctx.isRebooting &&
		!ctx.isUpdating &&
		!ctx.isPending
	) {
		deviceStateConfig.type = offlineStateType;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'disabled',
			bold: false,
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.iconColor = 'disabled';
		deviceStateConfig.icon = 'fal fa-circle-xmark';
	} else if (ctx.isPending) {
		deviceStateConfig.type = 1;

		if (is_gen_z(device)) {
			deviceStateConfig.description = {
				value: capFirstChar(
					_format('z-wave_inclusion_state_descovering', {
						default: 'Discovering device',
					}),
				),
				type: 'primary',
			};

			if (typeof ctx.zWaveInclusionStatus !== 'undefined') {
				deviceStateConfig.description = {
					value: capFirstChar(
						_format('z-wave_inclusion_state_started', {
							default: 'Inclusion started',
						}),
					),
					type: 'primary',
				};
			}
		}

		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'primary',
		};

		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.icon = 'fal fa-cloud-question';
		deviceStateConfig.iconColor = 'color-3 ';
	} else if (ctx.isCalibrating || ctx.isDimmerCalibrating) {
		deviceStateConfig.type = 4;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'primary',
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = true;
		deviceStateConfig.icon = undefined;
	} else if (ctx.isUpdating) {
		deviceStateConfig.type = 3;
		if ((ctx.updatingEntry?.prog ?? 0) < 100 || isWallDisplay(device?.type) || is_gen_1(device)) {
			deviceStateConfig.description = {
				value: availableStatuses(device)[deviceStateConfig.type],
				type: 'primary',
			};
		} else {
			deviceStateConfig.description = {
				value: capFirstChar(
					get(format)('progress_state_update_complete', {
						default: 'Firmware update complete',
					}),
				),
				type: 'success',
			};
		}
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = true;
		deviceStateConfig.icon = undefined;
	} else if (ctx.isRebooting) {
		deviceStateConfig.type = 2;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'primary',
		};

		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = true;
		deviceStateConfig.icon = undefined;
	} else if (ctx.isLocked) {
		deviceStateConfig.type = 5;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'warning',
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.icon = 'fal fa-user-lock';
		deviceStateConfig.iconColor = 'warning';
	} else if (ctx.isUnauthorized) {
		deviceStateConfig.type = 9;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'warning',
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.icon = 'fal fa-lock';
		deviceStateConfig.iconColor = 'warning';
	} else if (ctx.isUpdatingWifi) {
		deviceStateConfig.type = 10;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'primary',
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = true;
		deviceStateConfig.icon = undefined;
	} else if (ctx.isAssociating) {
		deviceStateConfig.type = 11;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'primary',
		};
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = true;
		deviceStateConfig.icon = undefined;
	} else if (checkDeviceForceUpdate(device)) {
		deviceStateConfig.type = 7;
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.description = {
			value: _format('new_fw_update_header', {
				default: 'Firmware update available',
			}),
			type: 'muted',
		};
	} else if (!accessibleVirtualDevice(device)) {
		deviceStateConfig.type = 14;
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'muted',
		};
	} else if (isExtractedDeviceComponentMissing(device, ctx.status)) {
		deviceStateConfig.type = 15;
		deviceStateConfig.hideSlotMain = true;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.icon = 'fal fa-circle-xmark';
		deviceStateConfig.description = {
			value: availableStatuses(device)[deviceStateConfig.type],
			type: 'muted',
		};
	} else {
		deviceStateConfig.type = defaultStateType;
		deviceStateConfig.subtitle = { ...defaultOptionalTexts };
		deviceStateConfig.hideSlotMain = false;
		deviceStateConfig.hideSlotRight = false;
		deviceStateConfig.isLoading = false;
		deviceStateConfig.description = { ...defaultOptionalTexts };
		deviceStateConfig.optional = { ...defaultOptionalTexts };
		deviceStateConfig.icon = undefined;
		deviceStateConfig.iconColor = undefined;
	}

	return deviceStateConfig;
}

export function buildDeviceStateContext(
	device: Device,
	deps: {
		loadingData: boolean;
		status: Record<string, unknown> | undefined;
		settings: Record<string, unknown> | undefined;
		isRebooting: boolean;
		isLocked: boolean;
		isPending: boolean;
		isUpdating: boolean;
		updatingEntry: { prog?: number } | undefined;
		isAssociating: boolean;
		isUpdatingWifi: boolean;
		zWaveInclusionStatus: unknown;
	},
): DeviceStateComputeContext {
	const mode = get_mode(device, deps.status, deps.settings);
	const isCalibrating = device.category == 'roller' && getIsCalibrationFromstatus(device, deps.status);
	const isDimmerCalibrating =
		mode === 'light' && getIsDimmerCalibratingFromStatus(device, deps.status);
	const isUnauthorized =
		(typeof deps.status?.awaiting_auth_code !== 'undefined' && deps.status?.awaiting_auth_code) ||
		(typeof deps.status?.sys?.awaiting_auth_code !== 'undefined' &&
			deps.status?.sys?.awaiting_auth_code);

	return {
		loadingData: deps.loadingData,
		status: deps.status,
		settings: deps.settings,
		isRebooting: deps.isRebooting,
		isLocked: deps.isLocked,
		isPending: deps.isPending,
		isUpdating: deps.isUpdating,
		updatingEntry: deps.updatingEntry,
		isCalibrating,
		isDimmerCalibrating,
		isUnauthorized,
		isAssociating: deps.isAssociating,
		isUpdatingWifi: deps.isUpdatingWifi,
		zWaveInclusionStatus: deps.zWaveInclusionStatus,
	};
}
